import React, {
    Fragment,
    useEffect,
    useState,
    useRef,
    useCallback,
} from 'react'
import { ArrowSmallIcon, Icon } from 'packages/eid-icons'
import {
    Box,
    Radio,
    Skeleton,
    styled,
    Typography,
    useTheme,
} from '@mui/material'
import {
    getFullImageUrl,
    TREE_VIEW_LEVELS,
    TREE_CHILD_VIEW_LEVELS,
    DEFAULT_EMPTY_UUID,
    checkIfAllChildsShowInTree,
} from 'utils'
import classNames from 'classnames'
import { useTranslation } from 'react-i18next'
import { useTreeFilterAPI } from 'hooks'
import ChildTreeNode from './ChildTreeNode'
import { Tooltip } from 'packages/eid-ui'
import { isNilOrEmpty } from 'packages/core'
import { useTreeFocus } from './TreeFocusContext'

const FolderMain = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    fontSize: '1.4rem',
    '& img': {
        width: '1.5rem',
        marginLeft: '0.5rem',
        cursor: 'pointer',
    },
    '&:focus-within': {
        outline: `0.2rem solid ${theme?.palette?.primary?.main}`,
        outlineOffset: '0.3rem',
    },
}))

const BusinesRoleAndLocationTreeBox = styled(Box)({
    marginTop: '1rem',
    position: 'relative',
    '& p': {
        fontSize: '1.4rem',
        marginLeft: '0.8rem',
        fontWeight: '600',
        color: '#767676',
    },
})

const FolderInnerItem = styled(Box)(({ theme }) => ({
    marginTop: '1rem',
    display: 'flex',
    alignItems: 'center',
    padding: '0.5rem 0.8rem 0.5rem 1.5rem',
    borderRadius: '0.5rem',
    '& img': {
        width: '1.5rem',
        marginLeft: '0.5rem',
    },
    '& p': {
        fontSize: '1.4rem',
        marginLeft: '0.8rem',
        cursor: 'pointer',
    },
    '&:focus-within': {
        outline: `0.2rem solid ${theme?.palette?.primary?.main}`,
        outlineOffset: '-0.2rem',
    },
}))

const SelectSection = styled(Box)({
    maxWidth: '30rem',
    '& p': {
        fontWeight: '500',
    },
})

const NodeLabel = styled(Typography)(({ theme }) => ({
    cursor: 'pointer',
    '& .search-highlight': {
        color: '#d36dbf',
        fontWeight: 600,
        textDecoration: 'underline',
    },
}))

const StyledRadio = styled(Radio)({
    padding: '0.2rem',
    maxHeight: '2rem',
    '&:hover': {
        backgroundColor: '#d2d2d9',
    },
})

const highlight = (text, searchKey) => {
    searchKey = new RegExp('(' + searchKey + ')', 'gi')
    return text.split(searchKey).map((o, i) =>
        i % 2 === 1 ? (
            <span className="search-highlight" key={i}>
                {o}
            </span>
        ) : (
            o
        ),
    )
}

const TreeNode = React.memo(
    ({
        explorer,
        handleSelect,
        value,
        disableAssignableCheck = false,
        highlightText = '',
        titleProp = 'friendlyName',
        valueProp = 'id',
        url,
        showCollapseIcon = true,
        showRadioToSelect = true,
        disableAssignableProp = 'isAssignable',
        onChildrenUpdate,
        showSuggestedToPass,
    }) => {
        const { t } = useTranslation()
        const [isOpen, setIsOpen] = useState(
            explorer?.rootId === DEFAULT_EMPTY_UUID && !explorer?.showInTree,
        )
        const [fetchChildren, setFetchChildren] = useState(false)
        const [children, setChildren] = useState(explorer?.children || [])
        const theme = useTheme()
        const nodeRef = useRef(null)
        const treeContext = useTreeFocus()
        const nodeId = explorer?.[valueProp]
        const isFocused = treeContext?.focusedNodeId === nodeId

        // Determine tabIndex based on focus state
        // If nothing is focused, first node gets tabIndex={0}, otherwise only focused node gets it
        let tabIndex = 0
        if (treeContext) {
            if (treeContext.focusedNodeId === null) {
                // Nothing focused yet - first visible node should be tabbable
                const firstNodeId = treeContext.getFirstVisibleNodeId?.()
                tabIndex = nodeId === firstNodeId ? 0 : -1
            } else {
                // Something is focused - only that node is tabbable
                tabIndex = isFocused ? 0 : -1
            }
        }

        // Control tooltip visibility based on focus state
        const [isHovered, setIsHovered] = useState(false)
        // Only show tooltip when actually focused (not just tabbable)
        const shouldShowTooltip = isHovered || isFocused

        // Get tooltip title based on node state
        const getTooltipTitle = useCallback(() => {
            if (explorer?.alreadyAssigned) {
                return t('AlreadyAssigned')
            }
            if (explorer?.isAssignable === false && disableAssignableCheck) {
                return t('NotAvailableForAssignment')
            }
            if (!explorer?.showInTree) {
                return t('NotForSelection')
            }
            return ''
        }, [explorer, disableAssignableCheck, t])

        useEffect(() => {
            setChildren(explorer?.children || [])
        }, [explorer])

        useEffect(() => {
            if (
                highlightText?.length >= 3 &&
                !isOpen &&
                explorer?.hasChildren &&
                explorer?.children?.length
            ) {
                setIsOpen(true)
            }
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [highlightText, explorer?.hasChildren])

        const { data, isFetching } = useTreeFilterAPI(
            url.replace(
                `levelsToLoad=${TREE_VIEW_LEVELS}`,
                `levelsToLoad=${TREE_CHILD_VIEW_LEVELS}`,
            ),
            explorer?.id,
            explorer?.id,
            '',
            TREE_VIEW_LEVELS,
            fetchChildren,
            showSuggestedToPass,
        )

        useEffect(() => {
            if (data && explorer?.id) {
                if (onChildrenUpdate) {
                    onChildrenUpdate(explorer.id, data?.children)
                } else {
                    setChildren(data?.children)
                }
            }
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [data])

        // Register/unregister node with focus context
        useEffect(() => {
            if (
                nodeRef.current &&
                nodeId &&
                treeContext &&
                explorer?.showInTree !== false
            ) {
                treeContext.registerNode(nodeId, nodeRef.current)
                return () => {
                    treeContext.unregisterNode(nodeId)
                }
            }
        }, [nodeId, treeContext, explorer?.showInTree])

        const handleToggle = useCallback(() => {
            setIsOpen(!isOpen)
            if (!isOpen) {
                if (
                    explorer.hasChildren &&
                    children.length === 0 &&
                    !isFetching
                ) {
                    setFetchChildren(!fetchChildren)
                }
            }
        }, [
            isOpen,
            explorer.hasChildren,
            children.length,
            isFetching,
            fetchChildren,
        ])

        const handleKeyDown = useCallback(
            (e) => {
                const isDisabled =
                    (explorer?.[disableAssignableProp] === false &&
                        disableAssignableCheck) ||
                    !explorer?.showInTree

                switch (e.key) {
                    case 'Enter':
                    case ' ':
                        e.preventDefault()
                        e.stopPropagation()
                        if (e.target.closest('[role="radio"]')) {
                            // Radio button will handle its own selection
                            return
                        }
                        if (!isDisabled) {
                            handleSelect(explorer)
                        }
                        break
                    case 'ArrowRight':
                        e.preventDefault()
                        e.stopPropagation()
                        if (explorer.hasChildren && !isOpen) {
                            handleToggle()
                        } else if (
                            explorer.hasChildren &&
                            isOpen &&
                            treeContext
                        ) {
                            // Move to first child
                            treeContext.moveFocus('next')
                        }
                        break
                    case 'ArrowLeft':
                        e.preventDefault()
                        e.stopPropagation()
                        if (explorer.hasChildren && isOpen) {
                            handleToggle()
                        }
                        break
                    case 'ArrowDown':
                        e.preventDefault()
                        e.stopPropagation()
                        if (treeContext) {
                            treeContext.moveFocus('next')
                        }
                        break
                    case 'ArrowUp':
                        e.preventDefault()
                        e.stopPropagation()
                        if (treeContext) {
                            treeContext.moveFocus('prev')
                        }
                        break
                    default:
                        break
                }
            },
            [
                explorer,
                isOpen,
                handleToggle,
                handleSelect,
                disableAssignableCheck,
                disableAssignableProp,
                treeContext,
            ],
        )

        return (
            <Fragment>
                {explorer?.showInTree !== false ||
                isNilOrEmpty(explorer) ||
                explorer?.id === DEFAULT_EMPTY_UUID ? (
                    <>
                        {explorer?.hasChildren ? (
                            <BusinesRoleAndLocationTreeBox>
                                {!(
                                    explorer?.rootId === DEFAULT_EMPTY_UUID &&
                                    !explorer?.showInTree
                                ) ? (
                                    <Tooltip
                                        title={getTooltipTitle()}
                                        open={
                                            getTooltipTitle()
                                                ? shouldShowTooltip
                                                : false
                                        }
                                    >
                                        <FolderMain
                                            ref={nodeRef}
                                            tabIndex={tabIndex}
                                            onKeyDown={handleKeyDown}
                                            onFocus={() => {
                                                if (treeContext && nodeId) {
                                                    treeContext.setFocusedNodeId(
                                                        nodeId,
                                                    )
                                                }
                                            }}
                                            onMouseEnter={() =>
                                                setIsHovered(true)
                                            }
                                            onMouseLeave={() =>
                                                setIsHovered(false)
                                            }
                                            role="treeitem"
                                            aria-expanded={
                                                explorer.hasChildren
                                                    ? isOpen
                                                    : undefined
                                            }
                                            aria-level={1}
                                            aria-selected={
                                                value === explorer?.[valueProp]
                                            }
                                            aria-disabled={
                                                (explorer?.[
                                                    disableAssignableProp
                                                ] === false &&
                                                    disableAssignableCheck) ||
                                                !explorer?.showInTree
                                            }
                                        >
                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    cursor: 'pointer',
                                                    gap: showRadioToSelect
                                                        ? '1rem'
                                                        : '0',
                                                }}
                                            >
                                                {explorer.hasChildren &&
                                                checkIfAllChildsShowInTree(
                                                    explorer,
                                                ) ? (
                                                    isOpen ? (
                                                        showCollapseIcon ? (
                                                            <Box
                                                                onClick={
                                                                    handleToggle
                                                                }
                                                                className="toggle-button"
                                                                tabIndex={-1}
                                                                role="button"
                                                                aria-label={t(
                                                                    'CollapseNode',
                                                                )}
                                                                sx={{
                                                                    '&:before':
                                                                        {
                                                                            content:
                                                                                '""',
                                                                            position:
                                                                                'absolute',
                                                                            width: '.12rem',
                                                                            backgroundColor:
                                                                                'transparent',
                                                                            borderLeft:
                                                                                '1px dotted lightgrey',
                                                                            top: '1.7rem',
                                                                            bottom: '1rem',
                                                                            left: '.7rem',
                                                                            transform:
                                                                                'translateX(-50%)',
                                                                        },
                                                                }}
                                                            >
                                                                <Box
                                                                    sx={{
                                                                        height: '1.5rem',
                                                                        '&:before':
                                                                            {
                                                                                content:
                                                                                    '""',
                                                                                position:
                                                                                    'absolute',
                                                                                width: '.12rem',
                                                                                backgroundColor:
                                                                                    'transparent',
                                                                                borderLeft:
                                                                                    '1px dotted lightgrey',
                                                                                top: '-.8rem',
                                                                                bottom: '1rem',
                                                                                left: '.7rem',
                                                                                transform:
                                                                                    'translateX(-50%)',
                                                                                height: '.8rem',
                                                                            },
                                                                        '&:after':
                                                                            {
                                                                                content:
                                                                                    '""',
                                                                                position:
                                                                                    'absolute',
                                                                                width: '.8rem',
                                                                                backgroundColor:
                                                                                    'transparent',
                                                                                borderTop:
                                                                                    '1px dotted lightgrey',
                                                                                top: '1rem',
                                                                                left: '2.2rem',
                                                                                transform:
                                                                                    'translateX(-50%)',
                                                                                height: '.12rem',
                                                                            },
                                                                    }}
                                                                >
                                                                    <Icon name="Collapse" />
                                                                </Box>
                                                            </Box>
                                                        ) : (
                                                            <Box
                                                                onClick={
                                                                    handleToggle
                                                                }
                                                                className="toggle-button"
                                                                tabIndex={-1}
                                                                role="button"
                                                                aria-label={t(
                                                                    'CollapseNode',
                                                                )}
                                                            >
                                                                <ArrowSmallIcon
                                                                    color="#60708f"
                                                                    fill="#60708f"
                                                                    direction={
                                                                        'down'
                                                                    }
                                                                />
                                                            </Box>
                                                        )
                                                    ) : showCollapseIcon ? (
                                                        <Box
                                                            onClick={
                                                                handleToggle
                                                            }
                                                            className="toggle-button"
                                                            tabIndex={-1}
                                                            role="button"
                                                            aria-label={t(
                                                                'ExpandNode',
                                                            )}
                                                            sx={{
                                                                height: '1.5rem',
                                                                '&:before': {
                                                                    content:
                                                                        '""',
                                                                    position:
                                                                        'absolute',
                                                                    width: '.12rem',
                                                                    borderLeft:
                                                                        '1px dotted grey',
                                                                    top: '-.8rem',
                                                                    bottom: '1rem',
                                                                    left: '.7rem',
                                                                    transform:
                                                                        'translateX(-50%)',
                                                                    height: '.8rem',
                                                                },
                                                                '&:after': {
                                                                    content:
                                                                        '""',
                                                                    position:
                                                                        'absolute',
                                                                    width: '.8rem',
                                                                    borderTop:
                                                                        '1px dotted grey',
                                                                    top: '1rem',
                                                                    bottom: '1rem',
                                                                    left: '2.2rem',
                                                                    transform:
                                                                        'translateX(-50%)',
                                                                    height: '.12rem',
                                                                },
                                                            }}
                                                        >
                                                            <Icon name="Expand" />
                                                        </Box>
                                                    ) : (
                                                        <Box
                                                            onClick={
                                                                handleToggle
                                                            }
                                                            className="toggle-button"
                                                            tabIndex={-1}
                                                            role="button"
                                                            aria-label={t(
                                                                'ExpandNode',
                                                            )}
                                                        >
                                                            <ArrowSmallIcon
                                                                color="#60708f"
                                                                fill="#60708f"
                                                                direction={
                                                                    'right'
                                                                }
                                                            />
                                                        </Box>
                                                    )
                                                ) : null}
                                                {(explorer?.imageUrl ||
                                                    explorer?.iconUrl) && (
                                                    <img
                                                        alt="folder-icons"
                                                        src={getFullImageUrl(
                                                            explorer?.imageUrl ||
                                                                explorer?.iconUrl,
                                                        )}
                                                    />
                                                )}
                                                {explorer?.icon && (
                                                    <Icon
                                                        name={explorer.icon}
                                                        width="1.6rem"
                                                        height="1.6rem"
                                                        color={
                                                            theme?.palette
                                                                ?.primary?.main
                                                        }
                                                    />
                                                )}
                                                {showRadioToSelect && (
                                                    <Box
                                                        sx={{
                                                            paddingLeft:
                                                                '2.2rem',
                                                            cursor: 'pointer',
                                                        }}
                                                        onClick={() =>
                                                            handleSelect(
                                                                explorer,
                                                            )
                                                        }
                                                    >
                                                        <StyledRadio
                                                            checkedIcon={
                                                                <Icon
                                                                    name="RadioFilled"
                                                                    color={
                                                                        theme
                                                                            ?.palette
                                                                            ?.primary
                                                                            ?.main
                                                                    }
                                                                />
                                                            }
                                                            icon={
                                                                <Icon name="Radio" />
                                                            }
                                                            checked={
                                                                value ===
                                                                    explorer?.[
                                                                        valueProp
                                                                    ] ?? false
                                                            }
                                                            disabled={
                                                                (explorer?.[
                                                                    disableAssignableProp
                                                                ] === false &&
                                                                    disableAssignableCheck) ||
                                                                !explorer?.showInTree
                                                            }
                                                        />
                                                    </Box>
                                                )}
                                            </Box>
                                            <NodeLabel
                                                className={classNames({
                                                    selectedNode:
                                                        explorer?.[valueProp] &&
                                                        value ===
                                                            explorer?.[
                                                                valueProp
                                                            ],
                                                    isDisabled:
                                                        (explorer?.[
                                                            disableAssignableProp
                                                        ] === false &&
                                                            disableAssignableCheck) ||
                                                        !explorer?.showInTree,
                                                })}
                                                onClick={() => {
                                                    if (
                                                        (explorer?.[
                                                            disableAssignableProp
                                                        ] === true ||
                                                            !disableAssignableCheck) &&
                                                        explorer?.showInTree
                                                    ) {
                                                        handleSelect(explorer)
                                                    }
                                                }}
                                            >
                                                {highlightText &&
                                                highlightText.length
                                                    ? highlight(
                                                          explorer?.[titleProp],
                                                          highlightText,
                                                      )
                                                    : explorer?.[titleProp]}
                                            </NodeLabel>
                                        </FolderMain>
                                    </Tooltip>
                                ) : null}

                                {isOpen && isFetching ? (
                                    <Box sx={{ paddingLeft: '1.5rem' }}>
                                        <Skeleton height={20} />
                                        <Skeleton height={20} />
                                    </Box>
                                ) : null}

                                {isOpen && children.length > 0 && (
                                    <Box role="group">
                                        {children.map((item, index) => (
                                            <div
                                                style={{
                                                    paddingLeft: !(
                                                        explorer?.rootId ===
                                                            DEFAULT_EMPTY_UUID &&
                                                        !explorer?.showInTree
                                                    )
                                                        ? '2.2rem'
                                                        : '',
                                                }}
                                                key={`${item?.id}-${index}`}
                                            >
                                                <ChildTreeNode
                                                    item={item}
                                                    handleSelect={handleSelect}
                                                    value={value}
                                                    disableAssignableCheck={
                                                        disableAssignableCheck
                                                    }
                                                    highlightText={
                                                        highlightText
                                                    }
                                                    url={url.replace(
                                                        `levelsToLoad=${TREE_VIEW_LEVELS}`,
                                                        `levelsToLoad=${TREE_CHILD_VIEW_LEVELS}`,
                                                    )}
                                                    titleProp={titleProp}
                                                    valueProp={valueProp}
                                                    showRadioToSelect={
                                                        showRadioToSelect
                                                    }
                                                    showCollapseIcon={
                                                        showCollapseIcon
                                                    }
                                                    disableAssignableProp={
                                                        disableAssignableProp
                                                    }
                                                    onChildrenUpdate={
                                                        onChildrenUpdate
                                                    }
                                                />
                                            </div>
                                        ))}
                                    </Box>
                                )}
                            </BusinesRoleAndLocationTreeBox>
                        ) : explorer?.[valueProp] && explorer?.[titleProp] ? (
                            <Tooltip
                                title={getTooltipTitle()}
                                open={
                                    getTooltipTitle()
                                        ? shouldShowTooltip
                                        : false
                                }
                            >
                                <FolderInnerItem
                                    className={SelectSection}
                                    ref={nodeRef}
                                    tabIndex={tabIndex}
                                    onKeyDown={handleKeyDown}
                                    onFocus={() => {
                                        if (treeContext && nodeId) {
                                            treeContext.setFocusedNodeId(nodeId)
                                        }
                                    }}
                                    onMouseEnter={() => setIsHovered(true)}
                                    onMouseLeave={() => setIsHovered(false)}
                                    role="treeitem"
                                    aria-level={1}
                                    aria-selected={
                                        value === explorer?.[valueProp]
                                    }
                                    aria-disabled={
                                        (explorer?.[disableAssignableProp] ===
                                            false &&
                                            disableAssignableCheck) ||
                                        !explorer?.showInTree
                                    }
                                >
                                    {(explorer?.imageUrl ||
                                        explorer?.iconUrl) && (
                                        <img
                                            alt="folder-icons"
                                            src={getFullImageUrl(
                                                explorer?.imageUrl ||
                                                    explorer?.iconUrl,
                                            )}
                                        />
                                    )}
                                    {showRadioToSelect && (
                                        <Box
                                            sx={{
                                                paddingLeft: '1rem',
                                                cursor: 'pointer',
                                            }}
                                            onClick={() =>
                                                handleSelect(explorer)
                                            }
                                        >
                                            <StyledRadio
                                                checkedIcon={
                                                    <Icon
                                                        name="RadioFilled"
                                                        color={
                                                            theme?.palette
                                                                ?.primary?.main
                                                        }
                                                    />
                                                }
                                                icon={<Icon name="Radio" />}
                                                checked={
                                                    value ===
                                                        explorer?.[valueProp] ??
                                                    false
                                                }
                                                disabled={
                                                    (explorer?.[
                                                        disableAssignableProp
                                                    ] === false &&
                                                        disableAssignableCheck) ||
                                                    !explorer?.showInTree
                                                }
                                            />
                                        </Box>
                                    )}
                                    <NodeLabel
                                        className={classNames({
                                            selectedNode:
                                                explorer?.[valueProp] &&
                                                value === explorer?.[valueProp],
                                            isDisabled:
                                                (explorer?.[
                                                    disableAssignableProp
                                                ] === false &&
                                                    disableAssignableCheck) ||
                                                !explorer?.showInTree,
                                        })}
                                        onClick={() => {
                                            if (
                                                (explorer?.[
                                                    disableAssignableProp
                                                ] === true ||
                                                    !disableAssignableCheck) &&
                                                explorer?.showInTree
                                            ) {
                                                handleSelect(explorer)
                                            }
                                        }}
                                    >
                                        {highlightText && highlightText.length
                                            ? highlight(
                                                  explorer?.[titleProp],
                                                  highlightText,
                                              )
                                            : explorer?.[titleProp]}
                                    </NodeLabel>
                                </FolderInnerItem>
                            </Tooltip>
                        ) : (
                            <Box>
                                <Typography
                                    sx={{
                                        textAlign: 'center',
                                    }}
                                >
                                    {t('Common_NoDataFound')}
                                </Typography>
                            </Box>
                        )}
                    </>
                ) : null}
            </Fragment>
        )
    },
)

export default TreeNode
